import React from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';

import FPlanningJourney from '../core/FPlanningJourney';

import { IBiePlanningContext } from './bieprofile.models';

export type FWorkbenchBreadcrumb = {
  label: string;
  onClick?: () => void;
};

export type FWorkbenchShellProps = {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  headerExtension?: React.ReactNode;
  breadcrumbs?: FWorkbenchBreadcrumb[];
  processStage?: React.ReactNode;
  summary?: React.ReactNode;
  filters?: React.ReactNode;
  actions?: React.ReactNode;
  loading?: boolean;
  saving?: boolean;
  dirty?: boolean;
  error?: string;
  success?: string;
  warning?: string;
  info?: string;
  phases?: any[];
  phasecode?: string;
  /** DB-authoritative Planning Cycle governance phase. */
  governancePhaseCode?: string;
  /** Identity of the currently rendered workbench. */
  workspacePhaseCode?: string;
  onBack?: () => void;
  onRefresh?: () => void;
  onHelp?: () => void;
  children: React.ReactNode;

  /** Shared persisted context; cycle-bound pages should pass this. */
  planningContext?: IBiePlanningContext | null;

  /** Enables the Planning Context strip in the workbench header. */
  showPlanningContext?: boolean;

  /** Opens the deliberate application-level cycle switcher. */
  onChangePlanningCycle?: () => void;

  /** True while the persisted context is being switched. */
  switchingPlanningContext?: boolean;
};

class FWorkbenchShell extends React.PureComponent<FWorkbenchShellProps> {
  private renderBreadcrumbs(): React.ReactNode {
    const breadcrumbs = this.props.breadcrumbs || [];
    if (!breadcrumbs.length) return null;

    return (
      <Breadcrumbs sx={{ mb: 1 }}>
        {breadcrumbs.map((item, index) => (
          <Typography
            key={`${item.label}-${index}`}
            variant="caption"
            color={item.onClick ? 'primary' : 'text.secondary'}
            sx={{ cursor: item.onClick ? 'pointer' : 'default' }}
            onClick={item.onClick}
          >
            {item.label}
          </Typography>
        ))}
      </Breadcrumbs>
    );
  }

  private renderStatus(): React.ReactNode {
    const { loading, saving, dirty } = this.props;

    if (saving) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="caption">Saving…</Typography>
        </Stack>
      );
    }

    if (loading) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="caption">Loading…</Typography>
        </Stack>
      );
    }

    if (dirty) {
      return (
        <Typography variant="caption" color="warning.main">
          ● Unsaved changes
        </Typography>
      );
    }

    return null;
  }

  private renderPlanningContext(): React.ReactNode {
    const {
      planningContext,
      showPlanningContext,
      onChangePlanningCycle,
      switchingPlanningContext,
    } = this.props;

    if (!showPlanningContext) return null;

    const selected = Boolean(planningContext?.pci);

    return (
      <Box
        sx={{
          mt: 1.5,
          pt: 1.5,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Stack spacing={0.75}>
            <Typography variant="overline" color="text.secondary">
              Planning Context
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {selected
                ? planningContext?.pcn || planningContext?.pcc
                : 'No Planning Cycle selected'}
            </Typography>

            {selected && (
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: 'wrap', rowGap: 1 }}
              >
                {(planningContext?.bpn || planningContext?.bpc) && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={planningContext.bpn || planningContext.bpc}
                  />
                )}

                {planningContext?.hdn && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Dataset: ${planningContext.hdn}`}
                  />
                )}

                {planningContext?.scn && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Scenario: ${planningContext.scn}`}
                  />
                )}
              </Stack>
            )}
          </Stack>

          {onChangePlanningCycle && (
            <Button
              size="small"
              variant={selected ? 'outlined' : 'contained'}
              startIcon={<SwapHorizIcon />}
              disabled={switchingPlanningContext}
              onClick={onChangePlanningCycle}
            >
              {switchingPlanningContext
                ? 'Switching…'
                : selected
                  ? 'Change Planning Cycle'
                  : 'Open Planning Cycle'}
            </Button>
          )}
        </Stack>
      </Box>
    );
  }

  private renderAlerts(): React.ReactNode {
    const { error, success, warning, info } = this.props;

    return (
      <>
        {error && <Alert severity="error">{error}</Alert>}
        {warning && <Alert severity="warning">{warning}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}
        {info && <Alert severity="info">{info}</Alert>}
      </>
    );
  }

  public render(): React.ReactNode {
    const props = this.props;
    const onBack = props.onBack;

    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <Paper elevation={1} sx={{ p: 2 }}>
            {this.renderBreadcrumbs()}

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'center' }}
              spacing={2}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                {this.props.onBack && (
                  <Tooltip title="Back" arrow>
                    <IconButton
                      disabled={this.props.loading}
                      onClick={this.props.onBack}
                    >
                      <ArrowBackOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {props.icon && (
                  <Box sx={{ display: 'flex', color: 'primary.main' }}>
                    {props.icon}
                  </Box>
                )}

                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    {props.title}
                  </Typography>
                  {props.subtitle && (
                    <Typography variant="body2" color="text.secondary">
                      {props.subtitle}
                    </Typography>
                  )}
                </Box>
              </Stack>

              <Stack spacing={1}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="flex-end"
                  flexWrap="wrap"
                >
                  {this.renderStatus()}

                  {props.onRefresh && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={props.onRefresh}
                    >
                      Refresh
                    </Button>
                  )}

                  {props.onHelp && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<HelpOutlineIcon />}
                      onClick={props.onHelp}
                    >
                      Help
                    </Button>
                  )}

                  {props.actions}
                </Stack>

                {props.headerExtension && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      mt: 0.5,
                    }}
                  >
                    {props.headerExtension}
                  </Box>
                )}
              </Stack>
            </Stack>
            {Boolean(props.phases?.length) && Boolean(props.phasecode) && (
              <Box
                sx={{
                  mt: 1.5,
                  pt: 1.25,
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                {/* <FPlanningJourney
                  phases={props.phases || []}
                  currentPhaseCode={props.phasecode}
                  compact
                  showCaption
                  caption="Planning Journey"
                  sx={{
                    alignItems: 'stretch',
                  }}
                /> */}
                <FPlanningJourney
                  phases={props.phases || []}

                  currentPhaseCode={props.phasecode}
                  governancePhaseCode={props.governancePhaseCode}
                  workspacePhaseCode={props.workspacePhaseCode}

                  planningStartPhaseCode="BUDGET_CONTROL"
                  planningEndPhaseCode="GOVERNANCE"

                  compact
                  showCaption
                  caption="Planning Journey"
                />
              </Box>
            )}

            {this.renderPlanningContext()}
          </Paper>

          {props.processStage && <Box>{props.processStage}</Box>}
          {props.summary && <Box>{props.summary}</Box>}
          {props.filters && <Box>{props.filters}</Box>}
          {this.renderAlerts()}
          <Box>{props.children}</Box>
        </Stack>
      </Box>
    );
  }
}

export default FWorkbenchShell;
